import React, { useRef, useEffect } from 'react';

import placeholderVideo from '../assets/videos/placeholder-video.mp4';

/**
 * VideoPlayer component that loops a video with smooth transitions
 * @param {string} src - Video source path
 * @param {string} className - Additional CSS classes
 * @param {boolean} autoPlay - Auto play video on mount (default: true)
 * @param {boolean} muted - Mute video (default: true)
 * @param {string} overlayText - Text to display over the video
 */
const VideoPlayer = ({
  src = placeholderVideo,
  className = '',
  autoPlay = true,
  muted = true,
  overlayText = 'Generating your first post',
}) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Handle smooth loop transition
    const handleTimeUpdate = () => {
      const buffer = 0.5; // Seconds before end to start fade/transition
      if (video.duration - video.currentTime <= buffer) {
        // Optionally add fade effect here
        video.style.opacity = '0.8';
      } else {
        video.style.opacity = '1';
      }
    };

    const handleEnded = () => {
      // Restart video smoothly
      video.currentTime = 0;
      video.play();
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  return (
    <div className='relative min-w-0  h-auto'>
      <video
        ref={videoRef}
        src={src}
        autoPlay={autoPlay}
        muted={muted}
        playsInline
        className={`w-full h-full object-cover transition-opacity duration-500 ${className}`}
        style={{ opacity: 1 }}
      >
        Your browser does not support the video tag.
      </video>

      {/* Overlay Text */}
      {overlayText && (
        <div className='absolute inset-0 flex items-center justify-center bg-black/10'>
          <div className='text-center px-6'>
            <h2 className='text-2xl  font-bold text-white mb-2 drop-shadow-lg'>{overlayText}</h2>
            <div className='flex items-center justify-center gap-2 mt-4'>
              <div
                className='w-2 h-2 bg-white rounded-full animate-bounce'
                style={{ animationDelay: '0ms' }}
              ></div>
              <div
                className='w-2 h-2 bg-white rounded-full animate-bounce'
                style={{ animationDelay: '150ms' }}
              ></div>
              <div
                className='w-2 h-2 bg-white rounded-full animate-bounce'
                style={{ animationDelay: '300ms' }}
              ></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
